import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { collectRefs, type StorefrontProduct } from '@aquilixshop/ui-react';
import { serialize, useBuilderStore } from '../state/builderStore';
import { catalogApi, productResponseToStorefront } from '../lib/catalogApi';
import { HttpError } from '../lib/errors';
import {
  forgetPickedProduct,
  readPickedProducts,
  rememberPickedProduct,
} from '../lib/pickedProductCache';

/** Joins/splits the collected ref ids — a space can't appear in an id, so it is a safe separator. */
const SEP = ' ';

/**
 * Resolve the document's product-picker refs for the EDIT canvas (feature 0023, editor side).
 *
 * The storefront resolves every `{kind:'product', id}` ref server-side (RLS-scoped) and hands the sections a
 * filled `resolved.products` map. The builder had no such step: `buildEditContext` only exposed the ROUTE
 * product, so every picker-bound section (`featured-product`, `collage`, `shoppable-image`, a tabbed-content
 * product card…) fell back to the built-in SAMPLE product while editing — the seller picked "OpenRun Pro 2"
 * and the canvas kept showing "Linen Weekend Shirt". This hook closes that gap: it walks the live document,
 * fetches each referenced product once (`GET /api/products/{id}`) and merges it into the render context.
 *
 * Design notes:
 *  - The ref-id LIST (not the document) drives the effect, so typing in a text field re-runs nothing.
 *  - FIRST PAINT IS THE REAL PRODUCT (founder report 2026-09-17). What is already known — this session's
 *    cache, or the cross-reload snapshot in `pickedProductCache` — is merged in a LAYOUT effect, i.e. before
 *    the browser paints, so the seller never sees the sample product flash under a section she has bound.
 *    The network round trip then runs in the background and corrects the snapshot.
 *  - Each id is fetched at most once per session (`attempted`) — including the ids seeded from the snapshot,
 *    which is exactly what keeps a renamed / unpublished / deleted product from surviving past that paint.
 *  - Only ACTIVE products are merged. A DRAFT/archived pick resolves to nothing on the live storefront, so
 *    resolving it here would make the editor promise a card the customer never sees. Such an id is DROPPED
 *    from the context (and from the snapshot), so the section falls back to the sample rather than keeping a
 *    card the shop will not show.
 *  - The whole cache is re-applied whenever the render context is rebuilt (theme undo, page switch, binding a
 *    preview product all mint a fresh context that drops `resolved`), which keeps the canvas self-healing.
 *    `mergeResolvedProducts` no-ops when nothing changed, so that loop settles after one pass.
 *  - Best-effort: an offline / 5xx failure leaves the section on what it has (it says nothing about the
 *    product); only a 404 evicts. The canvas never throws.
 */
export function usePickedProducts(): void {
  const order = useBuilderStore((s) => s.order);
  const sectionsById = useBuilderStore((s) => s.sectionsById);
  const meta = useBuilderStore((s) => s.meta);
  const renderContext = useBuilderStore((s) => s.renderContext);
  const mergeResolvedProducts = useBuilderStore((s) => s.mergeResolvedProducts);
  const dropResolvedProducts = useBuilderStore((s) => s.dropResolvedProducts);

  // Every keystroke mints a new `sectionsById`, but the REF LIST rarely changes — collapse it to a string so
  // the effect below only re-runs when a picker ref is actually added or removed.
  const idsKey = useMemo(
    () => collectRefs(serialize({ order, sectionsById, meta })).productIds.join(SEP),
    [order, sectionsById, meta],
  );
  const ids = useMemo(() => (idsKey ? idsKey.split(SEP) : []), [idsKey]);

  // Seeded from the cross-reload snapshot (store-scoped) so the very first render already has the bound
  // products in hand — see the hook doc. Lazily, because reading storage on every render would be wasteful.
  const cache = useRef<Map<string, StorefrontProduct> | null>(null);
  if (cache.current === null) cache.current = new Map(Object.entries(readPickedProducts()));
  const attempted = useRef(new Set<string>());

  const known = (list: string[]): Record<string, StorefrontProduct> => {
    const out: Record<string, StorefrontProduct> = {};
    for (const id of list) {
      const product = cache.current?.get(id);
      if (product) out[id] = product;
    }
    return out;
  };

  // BEFORE PAINT: apply everything already known (the snapshot on a cold load; the session cache after a
  // context rebuild, a page switch back or an undo). A plain effect here runs after the browser has painted,
  // which is precisely the sample-product flash this closes.
  useLayoutEffect(() => {
    if (ids.length === 0) return;
    // `known` reads a ref, so it needs no dep; `ids` / `renderContext` are what change the outcome.
    mergeResolvedProducts(known(ids));
  }, [ids, renderContext, mergeResolvedProducts]);

  // REVALIDATION. Deliberately does NOT depend on `renderContext`: re-applying the cache is the layout
  // effect's job above, and a re-run here would cancel the in-flight revalidation every time that merge
  // rebuilt the context — which is exactly how a deleted product used to survive its own 404. There is no
  // cancellation flag for the same reason: the target is a global store, both writes are idempotent (the
  // `attempted` set fires each id once, and both merges no-op when nothing changed), and a canvas that
  // remounts re-applies the cache anyway.
  useEffect(() => {
    if (ids.length === 0) return;

    // Revalidate every id ONCE per session — the ones served from the snapshot included, or a product the
    // seller renamed (or deleted) in the dashboard would keep its stale card for the whole session.
    const pending = ids.filter((id) => !attempted.current.has(id));
    if (pending.length === 0) return;
    for (const id of pending) attempted.current.add(id);

    const gone: string[] = [];
    void Promise.all(
      pending.map(async (id) => {
        try {
          const product = productResponseToStorefront(await catalogApi.getProduct(id));
          // Mirror the live storefront: only a published product resolves (see the hook doc).
          if (product.status.toUpperCase() !== 'ACTIVE') {
            cache.current?.delete(id);
            forgetPickedProduct(id);
            gone.push(id);
            return;
          }
          cache.current?.set(id, product);
          rememberPickedProduct(product);
        } catch (e) {
          // A 404 is DEFINITIVE — the product is gone. Drop the remembered copy, because an editor that keeps
          // painting a deleted product is lying about what the shop will show. Any other failure (offline,
          // 5xx, an expired session) says nothing about the product, so the snapshot stays and the section
          // keeps the card it has. Either way this id is not retried in this session.
          if (e instanceof HttpError && e.status === 404) {
            cache.current?.delete(id);
            forgetPickedProduct(id);
            gone.push(id);
          }
        }
      }),
    ).then(() => {
      mergeResolvedProducts(known(pending));
      if (gone.length > 0) dropResolvedProducts(gone);
    });
  }, [ids, mergeResolvedProducts, dropResolvedProducts]);
}
